/**
 * CreateReservationForm — the WIRED create-reservation feature (SCRUM-337).
 *
 * A `modules/booking` feature composing React Hook Form + a Zod resolver over a
 * LOCAL form schema that LAYERS on `createReservationSchema` (@artemis/validators —
 * never redefined; FE rule §4) with form-only fields: `firstName`, `lastName`,
 * the property-picker selection, the cross-field `checkOut > checkIn`, the
 * conditional `heardAbout` requirement, and `adults >= 1`. Guest email + phone
 * are REQUIRED here per product requirements even though the wire allows null.
 *
 * ## Data seams
 *   - **Property picker** — a `FormSelect` whose options come from the property
 *     slice's `usePropertyNameLookup()` (id → display name; the sanctioned
 *     cross-slice read surface). A phone-in agent picks by NAME, never uuid. The
 *     selected `propertyId` feeds `usePropertySalesInfo(propertyId)` for the
 *     sales-info panel.
 *   - **Reservation type** — a `FormSelect` over `useReservationTypes()` labels;
 *     the selected type carries the server-computed `heardAboutRequired` flag.
 *   - **heardAbout** — a `FormSelect` over `HEARD_ABOUT_WIRE_VALUES`
 *     (@artemis/validators; FE-authored labels, no blank). REQUIRED iff
 *     `selectedType?.heardAboutRequired === true` (null / untyped → required).
 *   - **Live quote** — the debounced stay inputs feed `usePreview(input)`; its
 *     states render as loading / invalidStay / forbidden / ready. A ready quote
 *     is projected by the pure `toQuoteView` and rendered via `LedgerLine` +
 *     `formatCents`. NO client-side money arithmetic; a null Cents is omitted.
 *
 * ## Occupancy (B1 seam)
 *   Adults + Children + Pets + Vehicles inputs. At submit the hook receives
 *   `adults` + `children` (it maps `guestCount = adults + children`,
 *   `childCount = children`) — the form NEVER computes `guestCount` in a wire
 *   body and NEVER sends a `children` key. `adults >= 1` is client-enforced.
 *
 * ## Submit
 *   Two buttons drive the SAME `useCreateReservation`: "Create quote" →
 *   `instantBook: false` (→ QUO); "Confirm booking" → `instantBook: true`
 *   (→ BKD, overlap → 409). On success → `navigate('/reservations/' + id)`;
 *   Cancel → `navigate('/reservations')`. Errors: 409 → "dates no longer
 *   available"; 422 → heardAbout / ChannelCollect message (the create hook's
 *   tagged `kind`). Booking OCC is a server-side 409 — NO If-Match.
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79)**, kit-composed (TextField /
 * Button / Card / Badge), a11y WCAG 2.1 AA, axe-clean light + dark.
 */

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { createReservationSchema, HEARD_ABOUT_WIRE_VALUES } from '@artemis/validators';
import { Button, Card, Icon, IconBtn, TextField } from '@sparta/ui-kit';
import { usePropertySalesInfo, usePropertyNameLookup } from '../../property/index.js';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';
import { usePreview } from '../api/use-preview.js';
import { useCreateReservation, type CreateInput } from '../api/use-create-reservation.js';
import { useReservationTypes } from '../api/use-reservation-types.js';
import { toQuoteView, type QuoteLineEntry } from '../api/to-quote-view.js';
import { LedgerLine } from '../_components/LedgerLine.js';
import { FormSelect, type FormSelectOption } from '../_components/FormSelect.js';
import { SalesInfoPanel } from '../_components/SalesInfoPanel.js';
import { PropertyAmenitiesSummary } from '../_components/PropertyAmenitiesSummary.js';
import { formatCents } from '../../../shared/formatters/entity-change/values.js';

/* ── local debounce (no shared hook exists yet; slice-internal) ──────────── */

const QUOTE_DEBOUNCE_MS = 350;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/* ── quote-warning FE-authored labels (values are the wire warning codes) ── */

/**
 * FE-authored human labels for the known preview warning codes. An unknown code
 * falls back to the raw `code` string so a new server warning is never silently
 * dropped — the `code` stays discoverable (and the RTL assertion matching the
 * raw code holds for any code we haven't labelled yet).
 */
const QUOTE_WARNING_LABELS: Record<string, string> = {
  CAPACITY_EXCEEDED: 'This stay exceeds the property’s occupancy — capacity exceeded.',
};

function quoteWarningLabel(code: string): string {
  return QUOTE_WARNING_LABELS[code] ?? code;
}

/* ── heardAbout FE-authored labels (values are the closed wire enum) ─────── */

const HEARD_ABOUT_LABELS: Record<(typeof HEARD_ABOUT_WIRE_VALUES)[number], string> = {
  Social: 'Social media',
  Email: 'Email campaign',
  OTA: 'Booking site (OTA)',
  Direct: 'Word of mouth / direct',
  GEO: 'Local / neighborhood',
  Search: 'Search engine',
};

const HEARD_ABOUT_OPTIONS: ReadonlyArray<
  FormSelectOption<(typeof HEARD_ABOUT_WIRE_VALUES)[number]>
> = HEARD_ABOUT_WIRE_VALUES.map((value) => ({
  id: value,
  label: HEARD_ABOUT_LABELS[value],
}));

/* ── local form schema — layers form-only fields onto the wire schema ────── */

/**
 * The wire count guards (a SUBSET of `createReservationSchema` — the single
 * source of truth, FE rule §4) reused for `petCount` / `vehicleCount` /
 * `childCount` so the count rules stay owned by @artemis/validators; we
 * reference `.shape.*` rather than restating the `int().min(0)` predicate.
 * `checkInDate` / `checkOutDate` are plain strings here (an HTML `type="date"`
 * emits a bare `YYYY-MM-DD` string that maps STRAIGHT to the branded
 * `LocalDate` at the wire boundary — the brand is applied server-side, not in
 * the form). Cross-field rules (`checkOut > checkIn`, conditional `heardAbout`,
 * `adults >= 1`) are layered in `.superRefine`. The schema's input === output
 * (no `.default()`, no brand) so RHF's string/number field bindings align.
 */
// `.removeDefault()` drops the wire schema's `.default(0)` (which would make
// the field optional on the form-input side and diverge input≠output) while
// keeping the reused `int().min(0)` count predicate owned by @artemis/validators.
const wireCount = createReservationSchema.shape.petCount.removeDefault();

const formSchema = z
  .object({
    propertyId: z.string().uuid({ message: 'Select a property.' }),
    checkInDate: z.string().min(1, 'Check-in is required.'),
    checkOutDate: z.string().min(1, 'Check-out is required.'),
    firstName: z.string().trim().min(1, 'First name is required.'),
    lastName: z.string().trim().min(1, 'Last name is required.'),
    email: z.string().trim().min(1, 'Email is required.'),
    phone: z.string().trim().min(1, 'Phone is required.'),
    adults: z.number().int().min(1, 'At least one adult is required.'),
    children: wireCount,
    petCount: wireCount,
    vehicleCount: wireCount,
    reservationType: z.string().nullable(),
    // heardAbout is one of the closed wire values, or '' (unset). Requiredness
    // is conditional on the selected type → enforced in superRefine.
    heardAbout: z.enum(HEARD_ABOUT_WIRE_VALUES).or(z.literal('')),
    // heardAboutRequired mirrors the selected type's server-computed flag so the
    // resolver can enforce the conditional requirement without reaching outside.
    heardAboutRequired: z.boolean(),
  })
  .superRefine((values, ctx) => {
    // ISO YYYY-MM-DD is zero-padded → lexicographic compare == date compare.
    if (
      values.checkInDate.length > 0 &&
      values.checkOutDate.length > 0 &&
      values.checkOutDate <= values.checkInDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['checkOutDate'],
        message: 'Check-out must be after check-in.',
      });
    }
    if (values.heardAboutRequired && values.heardAbout === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['heardAbout'],
        message: 'Tell us how the guest heard about us.',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
  propertyId: '',
  checkInDate: '',
  checkOutDate: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  adults: 1,
  children: 0,
  petCount: 0,
  vehicleCount: 0,
  reservationType: null,
  heardAbout: '',
  heardAboutRequired: true,
};

/* ── error copy (mapped from the create hook's tagged kind) ──────────────── */

function errorMessageFor(kind: string, code?: string): string {
  switch (kind) {
    case 'conflict':
      return 'Those dates are no longer available. Pick a different stay.';
    case 'illegal':
      return illegalMessageFor(code);
    case 'forbidden':
      return 'You don’t have permission to create this reservation.';
    case 'notFound':
      return 'That property could not be found.';
    default:
      return 'Something went wrong creating the reservation. Try again.';
  }
}

/**
 * A SPECIFIC message per 422 `error` code (booking's error filter emits
 * `{ error: '<code>' }`). The generic fallback covers an unlabelled / new code
 * so a future server rejection is never silently mis-explained. Names no
 * internal identifiers — plain guidance only.
 */
function illegalMessageFor(code?: string): string {
  switch (code) {
    case 'channel_collect_not_locally_creatable':
      return 'Channel bookings (like Airbnb) sync in from the channel automatically — they can’t be created here. For a direct booking, choose a type such as VRBO, or leave it untyped.';
    case 'heard_about_required':
      return 'Select how the guest heard about you — it’s required for this reservation type.';
    case 'unknown_reservation_type':
      return 'That reservation type can’t be booked from here.';
    default:
      return 'This reservation can’t be created as entered. Check the reservation type and the required fields.';
  }
}

/* ── component ───────────────────────────────────────────────────────────── */

export function CreateReservationForm(): React.ReactElement {
  const navigate = useNavigate();
  const create = useCreateReservation();
  const typesState = useReservationTypes();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(formSchema),
  });

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [salesOpen, setSalesOpen] = React.useState(false);

  // ── reservation-type catalog → picker options + selected flag ────────────
  const catalog = typesState.status === 'ready' ? typesState.data : [];
  const typeOptions: ReadonlyArray<FormSelectOption<string>> = catalog.map((t) => ({
    id: t.internalKey,
    label: t.displayName,
  }));

  const selectedTypeKey = watch('reservationType');
  const selectedType = catalog.find((t) => t.internalKey === selectedTypeKey);
  // Null / untyped type → heardAbout required (the server's null-default rule).
  const heardAboutRequired = selectedType?.heardAboutRequired ?? true;

  // Keep the resolver's mirror field in sync with the selected type's flag so
  // the conditional-requirement superRefine sees the current predicate.
  React.useEffect(() => {
    setValue('heardAboutRequired', heardAboutRequired);
  }, [heardAboutRequired, setValue]);

  // ── property picker options (id → name) ──────────────────────────────────
  const nameLookup = usePropertyNameLookup();
  const propertyOptions: ReadonlyArray<FormSelectOption<string>> =
    nameLookup.status === 'ready'
      ? Array.from(nameLookup.nameById.entries()).map(([id, name]) => ({
          id,
          label: name,
        }))
      : [];

  const propertyId = watch('propertyId');
  const salesInfo = usePropertySalesInfo(propertyId.length > 0 ? propertyId : null);

  // ── live-quote inputs (debounced) ────────────────────────────────────────
  const checkInDate = watch('checkInDate');
  const checkOutDate = watch('checkOutDate');
  const adults = Number(watch('adults')) || 0;
  const children = Number(watch('children')) || 0;
  const petCount = Number(watch('petCount')) || 0;
  const vehicleCount = Number(watch('vehicleCount')) || 0;

  const previewInputRaw = React.useMemo(
    () => ({
      propertyId,
      checkInDate,
      checkOutDate,
      guestCount: adults + children,
      childCount: children,
      petCount,
      vehicleCount,
      reservationType: selectedTypeKey ?? null,
      bookingChannel: null as string | null,
    }),
    [
      propertyId,
      checkInDate,
      checkOutDate,
      adults,
      children,
      petCount,
      vehicleCount,
      selectedTypeKey,
    ],
  );
  const previewInput = useDebouncedValue(previewInputRaw, QUOTE_DEBOUNCE_MS);
  const preview = usePreview(previewInput);

  // ── submit (both buttons share this; instantBook set per-click) ──────────
  const instantBookRef = React.useRef(false);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    const input: CreateInput = {
      propertyId: values.propertyId,
      checkInDate: values.checkInDate,
      checkOutDate: values.checkOutDate,
      adults: values.adults,
      children: values.children,
      petCount: values.petCount,
      vehicleCount: values.vehicleCount,
      reservationType: values.reservationType,
      bookingChannel: null,
      instantBook: instantBookRef.current,
      guest: {
        displayName: `${values.firstName} ${values.lastName}`.trim(),
        email: values.email,
        phone: values.phone,
      },
      // Omit heardAbout entirely when unset (never send "").
      ...(values.heardAbout !== '' ? { heardAbout: values.heardAbout } : {}),
    };

    try {
      const result = await create.mutateAsync(input);
      navigate(`/reservations/${result.reservationId}`);
    } catch (err) {
      const kind =
        typeof err === 'object' && err !== null && 'kind' in err
          ? String((err as { kind: unknown }).kind)
          : 'error';
      // The machine `error` code (booking filter) → a SPECIFIC 422 message.
      const code =
        typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        typeof (err as { code: unknown }).code === 'string'
          ? (err as { code: string }).code
          : undefined;
      setSubmitError(errorMessageFor(kind, code));
    }
  });

  const submitWith = (instantBook: boolean): void => {
    instantBookRef.current = instantBook;
    void onSubmit();
  };

  const isPending = create.status === 'pending';

  return (
    <>
      {/* Padded, centered content column (design reservations.jsx:336 —
          28/40/80 desktop, 20/16/80 compact, maxWidth 1180 ≈ max-w-6xl). The
          mobile slide-out overlay lives OUTSIDE this content column (below)
          so its backdrop covers the full page, not just the padded content. */}
      <div
        data-testid="create-reservation-form"
        className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 pt-5 pb-20 sm:px-10 sm:pt-7"
      >
        {/* Page chrome (design reservations.jsx:337-339): back link + title + sub. */}
        <div>
          <Button
            variant="quiet"
            size="sm"
            className="-ml-2"
            onClick={() => navigate('/reservations')}
          >
            <Icon name="chevLeft" size={16} aria-hidden />
            <span className="ml-1">Reservations</span>
          </Button>
          <h1 className="m-0 mt-2 text-2xl font-semibold tracking-[-0.025em] text-[color:var(--fg-primary)]">
            New reservation
          </h1>
          <p className="m-0 mt-1.5 text-sm text-[color:var(--fg-secondary)]">
            Rapid entry for phone-in &amp; walk-in bookings.
          </p>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <form
            noValidate
            aria-label="Create reservation"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
            className="flex min-w-0 flex-1 flex-col gap-6"
          >
            {submitError !== null ? (
              <div
                role="alert"
                aria-live="assertive"
                className="rounded-xl border border-solid border-[var(--fg-danger)] bg-[var(--bg-surface)] p-3 text-sm text-[color:var(--fg-secondary)]"
              >
                {submitError}
              </div>
            ) : null}

            {/* ── Property + stay ──────────────────────────────────────────── */}
            <Card asChild>
              <section aria-labelledby="cr-stay-heading" className="flex flex-col gap-4">
                <h2
                  id="cr-stay-heading"
                  className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
                >
                  Property &amp; stay
                </h2>

                <div className="flex items-end justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <FormSelect
                      id="cr-property"
                      label="Property"
                      value={propertyId as string}
                      onChange={(next) => setValue('propertyId', next)}
                      options={propertyOptions}
                      placeholder="Select a property"
                      required
                      error={errors.propertyId?.message}
                    />
                  </div>
                  <div className="lg:hidden">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSalesOpen(true)}
                      disabled={propertyId.length === 0}
                    >
                      Sales info
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextField
                    type="date"
                    label="Arrival"
                    error={errors.checkInDate?.message}
                    {...register('checkInDate')}
                  />
                  <TextField
                    type="date"
                    label="Departure"
                    error={errors.checkOutDate?.message}
                    {...register('checkOutDate')}
                  />
                </div>
              </section>
            </Card>

            {/* ── Occupancy ────────────────────────────────────────────────── */}
            <Card asChild>
              <section aria-labelledby="cr-occ-heading" className="flex flex-col gap-4">
                <h2
                  id="cr-occ-heading"
                  className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
                >
                  Occupancy
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <TextField
                    type="number"
                    inputMode="numeric"
                    min={1}
                    label="Adults"
                    error={errors.adults?.message}
                    {...register('adults', { valueAsNumber: true })}
                  />
                  <TextField
                    type="number"
                    inputMode="numeric"
                    min={0}
                    label="Children"
                    error={errors.children?.message}
                    {...register('children', { valueAsNumber: true })}
                  />
                  <TextField
                    type="number"
                    inputMode="numeric"
                    min={0}
                    label="Pets"
                    error={errors.petCount?.message}
                    {...register('petCount', { valueAsNumber: true })}
                  />
                  <TextField
                    type="number"
                    inputMode="numeric"
                    min={0}
                    label="Vehicles"
                    error={errors.vehicleCount?.message}
                    {...register('vehicleCount', { valueAsNumber: true })}
                  />
                </div>
              </section>
            </Card>

            {/* ── Guest ────────────────────────────────────────────────────── */}
            <Card asChild>
              <section aria-labelledby="cr-guest-heading" className="flex flex-col gap-4">
                <h2
                  id="cr-guest-heading"
                  className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
                >
                  Guest
                </h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <TextField
                    label="Guest first name"
                    error={errors.firstName?.message}
                    {...register('firstName')}
                  />
                  <TextField
                    label="Guest last name"
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                  <TextField
                    type="email"
                    label="Email"
                    error={errors.email?.message}
                    {...register('email')}
                  />
                  <TextField
                    type="tel"
                    label="Phone"
                    error={errors.phone?.message}
                    {...register('phone')}
                  />
                </div>
              </section>
            </Card>

            {/* ── Type + provenance ────────────────────────────────────────── */}
            <Card asChild>
              <section aria-labelledby="cr-type-heading" className="flex flex-col gap-4">
                <h2
                  id="cr-type-heading"
                  className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
                >
                  Booking details
                </h2>
                <FormSelect
                  id="cr-type"
                  label="Reservation type"
                  value={(selectedTypeKey ?? '') as string}
                  onChange={(next) => setValue('reservationType', next === '' ? null : next)}
                  options={typeOptions}
                  placeholder="Untyped"
                  disabled={typesState.status !== 'ready'}
                />
                <FormSelect
                  id="cr-heard-about"
                  label="Heard about us"
                  value={watch('heardAbout')}
                  onChange={(next) =>
                    setValue('heardAbout', next as (typeof HEARD_ABOUT_WIRE_VALUES)[number] | '')
                  }
                  options={HEARD_ABOUT_OPTIONS}
                  placeholder="Select a source"
                  required={heardAboutRequired}
                  hint={heardAboutRequired ? 'Mandatory — no blank or N/A allowed.' : undefined}
                  error={errors.heardAbout?.message}
                />
              </section>
            </Card>

            {/* ── Actions ──────────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
              <Button type="button" variant="ghost" onClick={() => navigate('/reservations')}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={isPending}
                onClick={() => submitWith(false)}
              >
                Create quote
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={isPending}
                onClick={() => submitWith(true)}
              >
                Confirm booking
              </Button>
            </div>
          </form>

          {/* ── Right rail: live quote + sales info ─────────────────────────── */}
          <div className="flex w-full flex-col gap-6 lg:w-80 lg:shrink-0">
            <QuotePanel preview={preview} />

            {/* Desktop persistent aside; mobile slide-out toggled by the button. */}
            <div className="hidden lg:block">
              {salesInfo.status === 'ready' ? (
                <SalesInfoPanel
                  name={salesInfo.data.name}
                  capacity={salesInfo.data.capacity}
                  compact={false}
                  open={false}
                  onClose={() => undefined}
                />
              ) : salesInfo.status === 'forbidden' || salesInfo.status === 'error' ? (
                <SalesInfoFallback />
              ) : null}
            </div>

            {/* Desktop-only read-only amenity chips beside the sales-info panel.
              A non-critical rail add-on: renders nothing unless the property's
              amenities load ready (see PropertyAmenitiesSummary). */}
            <div className="hidden lg:block">
              <PropertyAmenitiesSummary propertyId={propertyId.length > 0 ? propertyId : null} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile slide-out lives at the root so its backdrop covers the page. */}
      {salesInfo.status === 'ready' ? (
        <div className="lg:hidden">
          <SalesInfoPanel
            name={salesInfo.data.name}
            capacity={salesInfo.data.capacity}
            compact={true}
            open={salesOpen}
            onClose={() => setSalesOpen(false)}
          />
        </div>
      ) : salesInfo.status === 'forbidden' || salesInfo.status === 'error' ? (
        <div className="lg:hidden">
          <SalesInfoFallbackSlideOut open={salesOpen} onClose={() => setSalesOpen(false)} />
        </div>
      ) : null}
    </>
  );
}

/* ── sales-info failure fallbacks (F-2) ──────────────────────────────────── */

/**
 * Honest desktop-rail fallback when `usePropertySalesInfo` is `forbidden` or
 * `error`. We never leave the rail blank (a dead affordance) — a `forbidden`
 * result (no `property.read`) and a load `error` both surface the same honest
 * "unavailable" copy. No internal identifier / status code leaks into the
 * user-facing string. Tokens-only, semantic `<aside>`.
 */
function SalesInfoFallback(): React.ReactElement {
  return (
    <aside
      data-testid="sales-info-fallback"
      className="rounded-2xl border border-solid border-[var(--border-subtle)] bg-[var(--bg-surface)] p-5"
    >
      <p className="m-0 text-sm text-[color:var(--fg-secondary)]">Property details unavailable.</p>
    </aside>
  );
}

/**
 * Mobile slide-out fallback — mirrors the SalesInfoPanel slide-out shape so the
 * "Sales info" button never opens an empty panel when sales-info is
 * `forbidden`/`error`. When `open={false}` it renders nothing.
 *
 * **A11y (WCAG 2.1.2 / 4.1.2).** The dialog is fully keyboard-operable and
 * focus-trapped, reusing the SAME primitives as `SalesInfoPanel`: the shared
 * `useFocusTrap` hook contains Tab focus inside the panel and returns focus to
 * the opener on close; a document-level Esc listener dismisses; an in-dialog
 * `IconBtn` "Close sales info" is the AT/keyboard close affordance. The
 * backdrop is an inert (`aria-hidden` + `tabIndex={-1}`) MOUSE-dismiss scrim.
 * The dialog carries `aria-modal` + `aria-labelledby` (its own heading).
 */
function SalesInfoFallbackSlideOut({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): React.ReactElement | null {
  const headingId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  // Focus trap engages only while the slide-out is open (the hook no-ops when
  // `enabled` is false) — mirrors SalesInfoPanel.
  useFocusTrap(dialogRef, { enabled: open });

  // Esc closes the OPEN slide-out. Document-level listener so an Esc press
  // anywhere while the modal is up dismisses it; the hook owns focus, the
  // caller owns close (per useFocusTrap docs).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        data-testid="sales-info-fallback-backdrop"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0"
        style={{
          background: 'color-mix(in oklab, var(--fg-primary) 40%, transparent)',
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        data-testid="sales-info-fallback-slideout"
        className="absolute inset-y-0 right-0 flex h-full w-full max-w-sm flex-col gap-4 overflow-y-auto bg-[var(--bg-surface)] p-5 shadow-[var(--shadow-xl)]"
      >
        <div className="flex items-center justify-between">
          <h2 id={headingId} className="m-0 text-base font-semibold text-[color:var(--fg-primary)]">
            Sales info
          </h2>
          <IconBtn label="Close sales info" onClick={onClose}>
            <Icon name="close" size={20} />
          </IconBtn>
        </div>
        <p className="m-0 text-sm text-[color:var(--fg-secondary)]">
          Property details unavailable.
        </p>
      </div>
    </div>
  );
}

/* ── live-quote panel ────────────────────────────────────────────────────── */

function QuotePanel({
  preview,
}: {
  readonly preview: ReturnType<typeof usePreview>;
}): React.ReactElement {
  return (
    <Card>
      <h2 className="m-0 mb-3 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]">
        Live quote
      </h2>
      <QuoteBody preview={preview} />
    </Card>
  );
}

function QuoteBody({
  preview,
}: {
  readonly preview: ReturnType<typeof usePreview>;
}): React.ReactElement {
  if (preview.status === 'loading') {
    return (
      <div aria-busy="true" aria-live="polite" data-testid="quote-loading">
        <span className="sr-only">Calculating quote…</span>
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              aria-hidden="true"
              className="inline-block h-5 w-full rounded bg-[var(--bg-sunken)]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (preview.status === 'invalidStay') {
    return (
      <p className="text-sm text-[color:var(--fg-secondary)]">
        Enter valid dates — check-out must be after check-in.
      </p>
    );
  }

  if (preview.status === 'forbidden') {
    return (
      <p className="text-sm text-[color:var(--fg-secondary)]">
        You don’t have permission to preview pricing for this property.
      </p>
    );
  }

  if (preview.status === 'error') {
    return (
      <p role="alert" className="text-sm text-[color:var(--fg-secondary)]">
        Couldn’t calculate a quote. Adjust the stay and try again.
      </p>
    );
  }

  const view = toQuoteView(preview.data);
  return <QuoteReady view={view} />;
}

function QuoteReady({
  view,
}: {
  readonly view: ReturnType<typeof toQuoteView>;
}): React.ReactElement {
  const intl = useIntl();
  return (
    <div className="flex flex-col gap-3">
      {view.warnings.length > 0 ? (
        <div
          role="alert"
          aria-live="polite"
          data-testid="quote-warnings"
          className="flex flex-col gap-1 rounded-xl border border-solid border-[var(--fg-warning-on-tint)] bg-[var(--bg-surface)] p-3"
        >
          {view.warnings.map((w, i) => (
            <p
              key={`warning-${i}`}
              data-warning={w.code}
              className="m-0 text-sm text-[color:var(--fg-secondary)]"
            >
              {quoteWarningLabel(w.code)}
            </p>
          ))}
        </div>
      ) : null}

      {view.rentLineItems.length > 0 ? (
        <section aria-label="Nightly rate">
          {view.rentLineItems.map((line, i) => (
            <QuoteLine key={`rent-${i}`} line={line} />
          ))}
        </section>
      ) : null}

      {view.feeLineItems.length > 0 ? (
        <section aria-label="Fees">
          {view.feeLineItems.map((line, i) => (
            <QuoteLine key={`fee-${i}`} line={line} />
          ))}
        </section>
      ) : null}

      {view.taxLineItems.length > 0 ? (
        <section aria-label="Taxes">
          {view.taxLineItems.map((line, i) => (
            <QuoteLine key={`tax-${i}`} line={line} />
          ))}
        </section>
      ) : null}

      <div className="border-t border-[var(--border-subtle)] pt-2">
        {view.grandTotalCents === null ? (
          <div className="flex items-baseline justify-between gap-4 py-1">
            <span className="text-sm font-semibold text-[color:var(--fg-secondary)]">Total</span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-[color:var(--fg-primary)]">
              {'—'}
            </span>
          </div>
        ) : (
          <div className="flex items-baseline justify-between gap-4 py-1">
            <span className="text-sm font-semibold text-[color:var(--fg-secondary)]">Total</span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-[color:var(--fg-primary)]">
              {formatCents(view.grandTotalCents, intl)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A single quote line. A `null` Cents is a real "amount not computed" state and
 * renders the neutral "—" marker — NEVER `formatCents(0)` (a real $0.00 and an
 * absent amount are distinct). Present amounts route through `LedgerLine`
 * (kit-composed, `formatCents` at the display boundary).
 */
function QuoteLine({ line }: { readonly line: QuoteLineEntry }): React.ReactElement {
  if (line.valueCents === null) {
    return (
      <div className="flex items-baseline justify-between gap-4 py-1">
        <span className="text-sm text-[color:var(--fg-secondary)]">{line.label}</span>
        <span className="shrink-0 text-sm tabular-nums text-[color:var(--fg-tertiary)]">{'—'}</span>
      </div>
    );
  }
  return <LedgerLine label={line.label} valueCents={line.valueCents} />;
}
